import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Copy, Printer, Columns } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empMedicalService } from '@/services/empMedicalService'
import { contractService } from '@/services/contractService'
import type { MedicalListItem } from '@/api/types/empMedical.types'
import type { EmployeeOption } from '@/api/types'

function formatDate(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

export const MedicalList: React.FC = () => {
  const { t } = useTranslation('common')
  const tm = (key: string) => t(`empMedical.${key}`)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [list, setList] = useState<MedicalListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employeeName: true,
    medicalCard: true,
    issueDate: true,
    expiryDate: true,
    medicalDetail: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empMedicalService.getAll(branchId, 1, 500)
        setList(res.data ?? [])
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setList([])
        if (!isNoData) toast.error(e?.response?.data?.message ?? tm('fetchFailed'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId])

  const getEmployeeId = (row: MedicalListItem): number | undefined => row.empId ?? row.employeeId

  const getEmployeeName = (empId?: number): string => {
    if (!empId) return '—'
    const emp = employees.find((e) => e.empId === empId)
    if (!emp) return t('empMedical.employeeFallback', { id: empId })
    return (
      (emp.fullName ?? [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()) ||
      t('empMedical.employeeFallback', { id: empId })
    )
  }

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(
      (r) =>
        getEmployeeName(getEmployeeId(r)).toLowerCase().includes(q) ||
        (r.medicalCard ?? '').toLowerCase().includes(q) ||
        (r.medicalDetail ?? '').toLowerCase().includes(q) ||
        (r.bloodGroup ?? '').toLowerCase().includes(q)
    )
  }, [list, searchQuery, employees, t])

  const paginatedRows = useMemo(
    () => filteredList.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    [filteredList, pageNumber, pageSize]
  )
  const filteredTotal = filteredList.length
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))
  const visibleColumnCount =
    [columnVisibility.employeeName, columnVisibility.medicalCard, columnVisibility.issueDate, columnVisibility.expiryDate, columnVisibility.medicalDetail, columnVisibility.createdDate, columnVisibility.action].filter(Boolean).length || 1

  const handleAdd = () => navigate('/medicals/new')
  const handleEdit = (id: number) => navigate(`/medicals/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(tm('deleteTitle'), tm('deleteConfirm'))
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empMedicalService.delete(id)
      await showSuccess(tm('deletedTitle'), tm('deletedMessage'))
      if (branchId) {
        const res = await empMedicalService.getAll(branchId, 1, 500)
        setList(res.data ?? [])
      }
    } catch (e: any) {
      await showError(tm('deleteFailedTitle'), e?.response?.data?.message ?? e?.message ?? tm('deleteFailedMessage'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = [
      tm('columnEmployeeName'),
      tm('columnMedicalCard'),
      tm('columnIssueDate'),
      tm('columnExpiryDate'),
      tm('columnMedicalDetail'),
      tm('columnCreatedDate'),
    ]
    const rows = filteredList.map((r) =>
      [
        getEmployeeName(getEmployeeId(r)),
        r.medicalCard ?? '',
        r.issueDate ? formatDate(r.issueDate) : '',
        r.expiryDate ? formatDate(r.expiryDate) : '',
        (r.medicalDetail ?? '').replace(/,/g, ' '),
        r.createdDate ? formatDate(r.createdDate) : '',
      ].join(',')
    )
    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = tm('csvFileName')
    a.click()
    URL.revokeObjectURL(url)
    toast.success(tm('csvDownloaded'))
  }

  const handleCopy = () => {
    const text = filteredList
      .map((r) =>
        [getEmployeeName(getEmployeeId(r)), r.medicalCard ?? '', r.issueDate ? formatDate(r.issueDate) : '', r.expiryDate ? formatDate(r.expiryDate) : '', (r.medicalDetail ?? '').slice(0, 50), r.createdDate ? formatDate(r.createdDate) : ''].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(text).then(() => toast.success(tm('copiedToClipboard')))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={tm('listTitle')} description={tm('listDescriptionShort')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{tm('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={tm('listTitle')}
        description={tm('listDescription')}
        action={{ label: tm('actionAdd'), onClick: handleAdd, icon: Plus }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-2" />
                  {tm('columnVisibility')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.employeeName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, employeeName: !!v }))}
                >
                  {tm('columnEmployeeName')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.medicalCard}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, medicalCard: !!v }))}
                >
                  {tm('columnMedicalCard')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.issueDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, issueDate: !!v }))}
                >
                  {tm('columnIssueDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.expiryDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, expiryDate: !!v }))}
                >
                  {tm('columnExpiryDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.medicalDetail}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, medicalDetail: !!v }))}
                >
                  {tm('columnMedicalDetail')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  {tm('columnCreatedDate')}
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.action}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, action: !!v }))}
                >
                  {tm('columnAction')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv}>
              <Download className="w-4 h-4 mr-2" />
              {tm('csv')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="w-4 h-4 mr-2" />
              {tm('copy')}
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              {tm('print')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnVisibility.employeeName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnEmployeeName')}
                    </TableHead>
                  )}
                  {columnVisibility.medicalCard && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnMedicalCard')}
                    </TableHead>
                  )}
                  {columnVisibility.issueDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnIssueDate')}
                    </TableHead>
                  )}
                  {columnVisibility.expiryDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnExpiryDate')}
                    </TableHead>
                  )}
                  {columnVisibility.medicalDetail && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnMedicalDetail')}
                    </TableHead>
                  )}
                  {columnVisibility.createdDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnCreatedDate')}
                    </TableHead>
                  )}
                  {columnVisibility.action && (
                    <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {tm('columnAction')}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {tm('loading')}
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      {tm('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {columnVisibility.employeeName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getEmployeeName(getEmployeeId(row))}
                        </TableCell>
                      )}
                      {columnVisibility.medicalCard && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.medicalCard ?? '—'}</TableCell>
                      )}
                      {columnVisibility.issueDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.issueDate)}</TableCell>
                      )}
                      {columnVisibility.expiryDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.expiryDate)}</TableCell>
                      )}
                      {columnVisibility.medicalDetail && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle max-w-[200px] truncate" title={row.medicalDetail ?? ''}>
                          {row.medicalDetail ?? '—'}
                        </TableCell>
                      )}
                      {columnVisibility.createdDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.createdDate)}</TableCell>
                      )}
                      {columnVisibility.action && (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(row.id!)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row.id!)}
                              disabled={deletingId === row.id}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              {t('empMedical.paginationShowing', {
                from: filteredTotal === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                to: Math.min(pageNumber * pageSize, filteredTotal),
                total: filteredTotal,
              })}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {t('empMedical.pageOf', { page: pageNumber, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages || loading || filteredTotal === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('common.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
